import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDaysIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';

export interface FollowUpDateRange {
  from: string;
  to: string;
}

interface FollowUpDateRangeFilterProps {
  value: FollowUpDateRange | null;
  onChange: (range: FollowUpDateRange | null) => void;
  className?: string;
}

const formatRangeLabel = (range: FollowUpDateRange) => {
  const fmt = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(range.from)} – ${fmt(range.to)}`;
};

const FollowUpDateRangeFilter: React.FC<FollowUpDateRangeFilterProps> = ({ value, onChange, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setDraftFrom(value?.from ?? '');
      setDraftTo(value?.to ?? '');
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleApply = () => {
    if (!draftFrom || !draftTo) return;
    if (draftFrom > draftTo) return;
    onChange({ from: draftFrom, to: draftTo });
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setDraftFrom('');
    setDraftTo('');
    setOpen(false);
  };

  const panel =
    open &&
    createPortal(
      <div
        ref={panelRef}
        className="fixed z-[9998] w-[min(100vw-2rem,20rem)] rounded-xl border border-border bg-card p-4 shadow-xl"
        style={{
          top: (anchorRef.current?.getBoundingClientRect().bottom ?? 0) + 8,
          left: Math.min(
            anchorRef.current?.getBoundingClientRect().left ?? 8,
            window.innerWidth - 320 - 8
          ),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Follow-up date range</p>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Show leads with a follow-up scheduled within this range.
        </p>
        <div className="space-y-3">
          <label className="block text-xs font-medium text-muted-foreground">
            From
            <input
              type="date"
              value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            To
            <input
              type="date"
              value={draftTo}
              min={draftFrom || undefined}
              onChange={(e) => setDraftTo(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          {value && (
            <Button variant="outlined" size="sm" onClick={handleClear}>
              Clear
            </Button>
          )}
          <Button size="sm" onClick={handleApply} disabled={!draftFrom || !draftTo || draftFrom > draftTo}>
            Apply
          </Button>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={value ? `Follow-up: ${formatRangeLabel(value)}` : 'Filter by follow-up date range'}
        aria-label="Filter by follow-up date range"
        className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm transition-colors ${
          value
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground'
        } ${className}`}
      >
        <CalendarDaysIcon className="h-4 w-4 shrink-0" />
        {value ? (
          <span className="max-w-[10rem] truncate text-xs font-medium">{formatRangeLabel(value)}</span>
        ) : null}
      </button>
      {panel}
    </>
  );
};

export default FollowUpDateRangeFilter;
